import { ChatMessage, Conversation, createChatMessage, createConversation } from '../domain/models';
import { sendConversationMessage, sendNewConversationMessage } from '../infrastructure/apiClient';
import { appendStreamingChunk } from './streamingUtils';

/**
 * Returns true if a Completed status chunk is just a sentinel completion phrase
 * that should not be displayed to the user.
 */
export function isSentinelCompletion(status: string, contentRaw: string): boolean {
  if (status !== 'Completed') return false;
  const trimmed = contentRaw.trim();
  return /^(stream\s+completed\.?|stream\s+complete\.?|completed\.?|complete\.?|done\.?|finished\.?|finish\.?|end\.?|ended\.?|ok\.?|success\.?|succeeded\.?|completed\s+successfully\.?)$/i.test(trimmed);
}

/**
 * Filters content based on sentinel completion phrases.
 */
export function filterSentinelContent(status: string, rawContent: string | undefined): string {
  const content = rawContent || '';
  return isSentinelCompletion(status, content) ? '' : content;
}

/**
 * Ensures a streaming placeholder system message exists at the end of the conversation.
 * Returns the updated messages array (new array reference) and id of the placeholder.
 */
export function ensureSystemPlaceholder(messages: ChatMessage[]): { messages: ChatMessage[]; placeholderId: string } {
  const last = messages[messages.length - 1];
  if (last && last.role === 'system' && last.content.length === 0 && last.meta?.placeholder) {
    return { messages: messages, placeholderId: last.id };
  }
  const placeholder = createChatMessage({ role: 'system', content: '', meta: { placeholder: true, streaming: true } });
  return { messages: [...messages, placeholder], placeholderId: placeholder.id };
}


/**
 * Creates a new conversation if needed and appends the user message.
 * Also ensures a system placeholder for the incoming assistant response.
 */
/**
 * Generic preparation for sending a prompt. Can optionally skip appending the user message (retry scenario).
 * NOTE: If appendUserMessage=false we still invoke the backend send so a new assistant response streams,
 * but we do NOT push the duplicate user message into local state to avoid clutter.
 */
export async function preparePromptSend(
  existing: Conversation | null,
  activeClientId: string,
  userText: string,
  onCreated?: (id: string) => void,
  options?: { appendUserMessage?: boolean; attachments?: string[] }
) {
  const trimmed = userText.trim();
  if (!trimmed) throw new Error('Cannot send empty user message');

  let conversation = existing;
  let created = false;
  let userMessage: ChatMessage | undefined;
  const appendUserMessage = options?.appendUserMessage !== false; // default true
  const attachments = options?.attachments;

  if (!conversation) {
    const dto = await sendNewConversationMessage(activeClientId, trimmed);
    conversation = createConversation({ id: dto.threadId });
    if (appendUserMessage) {
      userMessage = createChatMessage({ role: 'user', content: trimmed, attachments });
      conversation.messages.push(userMessage);
    }
    created = true;
    onCreated?.(conversation.id);
  } else {
    const dto = await sendConversationMessage(activeClientId, conversation.id, 'user', trimmed);
    if (appendUserMessage) {
      userMessage = createChatMessage({ id: dto.id, role: dto.role as any, content: dto.content, createdAt: new Date(dto.createdAt), attachments });
      conversation.messages.push(userMessage);
    }
  }

  // Ensure system placeholder for streaming assistant response
  const ensured = ensureSystemPlaceholder(conversation.messages);
  conversation.messages = ensured.messages;

  return { conversation, userMessage, created };
}

// Backwards compatibility wrapper (existing callers)
export async function prepareUserSend(existing: Conversation | null, activeClientId: string, userText: string, onCreated?: (id: string) => void, attachments?: string[]) {
  return preparePromptSend(existing, activeClientId, userText, onCreated, { appendUserMessage: true, attachments });
}


/**
 * Applies a streaming update from SignalR to the conversation in a pure manner.
 * Does not modify external React state directly; caller decides side-effects.
 */
export function applyStreamingUpdate(conversation: Conversation, status: string, rawContent: string | undefined) {
  const msgs = [...conversation.messages];
  let last = msgs[msgs.length - 1];
  const effectiveContent = filterSentinelContent(status, rawContent);
  let stopSending = false;
  let aiFailed = false;

  if (status === 'Started') {
    // Ensure placeholder
    if (!last || last.role !== 'system' || last.content.length > 0) {
      const placeholder = createChatMessage({ role: 'system', content: '', meta: { placeholder: true, streaming: true } });
      msgs.push(placeholder);
    }
    return { conversation: { ...conversation, messages: msgs }, stopSending, aiFailed };
  }

  if (status === 'Failed') {
    aiFailed = true;
    stopSending = true;
    return { conversation, stopSending, aiFailed }; // no content modifications on fail
  }

  if (!last || last.role !== 'system') {
    // create new system message container
    const sysMsg = createChatMessage({ role: 'system', content: effectiveContent || '', meta: { streaming: status !== 'Completed' } });
    msgs.push(sysMsg);
  } else {
    const appended = appendStreamingChunk(last.content, effectiveContent || '');
    const updated: ChatMessage = { ...last, content: appended, meta: { ...(last.meta || {}), streaming: status !== 'Completed', placeholder: undefined } };
    msgs[msgs.length - 1] = updated;
  }

  if (status === 'Completed') {
    stopSending = true;
  } else if (effectiveContent) {
    // first meaningful chunk -> caller may stop showing loader
    stopSending = true;
  }

  return { conversation: { ...conversation, messages: msgs }, stopSending, aiFailed };
}
